// Number, duration and date formatting for the panel (Spanish locale, compact for large values).

const LOCALE = 'es-ES';
const intFmt = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const compactFmt = new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 });

export const DASH = '—';

export function fmtInt(v: number | null | undefined): string {
  return v === null || v === undefined || !Number.isFinite(v) ? DASH : intFmt.format(v);
}

/** 1.284 / 12,9 mil / 4,2 M — for tile values and axis ticks. */
export function fmtCompact(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  return Math.abs(v) < 10_000 ? fmtNum(v, Math.abs(v) < 10 && !Number.isInteger(v) ? 2 : Math.abs(v) < 100 && !Number.isInteger(v) ? 1 : 0) : compactFmt.format(v);
}

export function fmtNum(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(v);
}

/** Rate in [0, 1] as a percentage. */
export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  return new Intl.NumberFormat(LOCALE, { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: digits }).format(v);
}

/** Signed relative change, e.g. +12,3 %. */
export function fmtDeltaPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  const s = fmtPct(Math.abs(v), digits);
  return v > 0 ? `+${s}` : v < 0 ? `−${s}` : s;
}

/** Difference of two rates in percentage points. */
export function fmtPp(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  const n = fmtNum(Math.abs(v * 100), digits);
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${n} pp`;
}

/** 45 s · 4 min 12 s · 1 h 05 min */
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return DASH;
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min${s % 60 ? ` ${s % 60} s` : ''}`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, '0')} min`;
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];

/** 2026-10-16 -> 16 oct */
export function fmtDay(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? ''}`;
}

/** ISO timestamp -> 16 oct 12:03:15 (browser timezone). */
export function fmtDateTime(iso: string | null | undefined, opts: { seconds?: boolean } = {}): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  const time = d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', second: opts.seconds === false ? undefined : '2-digit' });
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${time}`;
}

/** ISO timestamp -> 12:03:15 (browser timezone). */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? DASH : d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function fmtAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'nunca';
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return `hace ${s} s`;
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86_400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86_400)} d`;
}

export function shortId(id: string | null | undefined, n = 8): string {
  return id ? id.slice(0, n) : DASH;
}

/** Clean axis maximum and ticks (0 included). */
export function niceTicks(max: number, count = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 1e-9; v += step) ticks.push(Number(v.toPrecision(12)));
  if (ticks[ticks.length - 1]! < max) ticks.push(Number((ticks[ticks.length - 1]! + step).toPrecision(12)));
  return ticks;
}

const moneyFmts = new Map<string, Intl.NumberFormat>();

/** Money in the game's reporting currency: 156,44 € (CPI-like unit costs: 3 decimals). */
export function fmtMoney(v: number | null | undefined, currency: string, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
  const key = `${currency}:${digits}`;
  let f = moneyFmts.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(LOCALE, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits });
    } catch {
      f = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    }
    moneyFmts.set(key, f);
  }
  return f.format(v);
}

/** "0,28–0,35 €" for an interval (null bounds -> open). */
export function fmtMoneyRange(low: number | null | undefined, high: number | null | undefined, currency: string, digits = 3): string {
  if (low === null || low === undefined) return DASH;
  return `${fmtNum(low, digits)}–${high === null || high === undefined ? '∞' : fmtMoney(high, currency, digits)}`;
}
